import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { DateInput } from '../components/ui/date-input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, UserCheck, Search, CheckCircle, Clock, ChevronDown, ChevronRight, X, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const euro = (n) =>
  (Number(n) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

// ISO YYYY-MM-DD -> gg/mm/aaaa (per la visualizzazione in tabella)
const dmy = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const TIPI = [
  { value: 'sub_agente', label: 'Sub-agente' },
  { value: 'condominio', label: 'Condominio' },
  { value: 'altro', label: 'Altra parte' },
];
const tipoLabel = (t) => (TIPI.find((x) => x.value === t) || {}).label || 'Beneficiario';
const TIPO_ORDER = { sub_agente: 0, condominio: 1, altro: 2 };
// Raggruppa per tipo (sub-agenti insieme, condomini insieme), mantenendo l'ordine di inserimento dentro ogni gruppo
const groupByTipo = (list) =>
  [...list].sort((a, b) => (TIPO_ORDER[a.tipo] ?? 9) - (TIPO_ORDER[b.tipo] ?? 9));

const emptyBenef = {
  tipo: 'condominio',
  nome: '',
  iban: '',
  percentuale: '',
  importo_da_pagare: '',
  pagato: false,
  data_pagamento: '',
};

const CURRENT_YEAR = new Date().getFullYear();

const emptyForm = {
  nome: '',
  cognome: '',
  anno: CURRENT_YEAR,
  iban: '',
  imponibile: '',
  percentuale: '',
  provvigioni_maturate: '',
  periodo_dal: '',
  periodo_al: '',
  pagato: false,
  data_pagamento: '',
  importo_pagato: '',
  beneficiari: [],
};

// Blocco riutilizzabile per un beneficiario (sub-agente / condominio / altra parte)
function BeneficiarioBlock({ value, onChange, onRemove, provvigioni }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const perc = parseFloat(value.percentuale);
  const autoImporto = !isNaN(perc);
  const importoCalc = autoImporto ? r2(provvigioni * perc / 100) : (parseFloat(value.importo_da_pagare) || 0);

  return (
    <div className="ml-3 border-l-2 border-teal-200 pl-4 space-y-3 rounded-r-lg bg-slate-50 py-3 pr-3">
      <div className="flex items-center gap-2">
        <Select value={value.tipo} onValueChange={(v) => set({ tipo: v })}>
          <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TIPI.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onRemove} title="Rimuovi">
          <X className="w-4 h-4 text-slate-500" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Nome</Label>
          <Input value={value.nome} onChange={(e) => set({ nome: e.target.value.toUpperCase() })} className="uppercase" />
        </div>
        <div className="space-y-2">
          <Label>IBAN <span className="text-slate-400 font-normal">— promemoria</span></Label>
          <Input value={value.iban} onChange={(e) => set({ iban: e.target.value.toUpperCase() })} className="uppercase" placeholder="ITxx..." />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Percentuale (%) <span className="text-slate-400 font-normal">— opz.</span></Label>
          <Input type="number" step="0.01" min="0" max="100" value={value.percentuale}
            placeholder="% sulla quota agente" onChange={(e) => set({ percentuale: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Importo da pagare (€) {autoImporto && <span className="text-teal-600 font-normal">— calcolato</span>}</Label>
          <Input type="number" step="0.01" min="0" value={autoImporto ? importoCalc : value.importo_da_pagare}
            disabled={autoImporto} onChange={(e) => set({ importo_da_pagare: e.target.value })} />
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg border p-3 bg-white">
        <Label>Pagato</Label>
        <Switch checked={value.pagato} onCheckedChange={(v) => set({ pagato: v })} />
      </div>
      {value.pagato && (
        <div className="space-y-2">
          <Label>Data pagamento</Label>
          <DateInput value={value.data_pagamento} onChange={(val) => set({ data_pagamento: val })} placeholder="gg/mm/aaaa" />
        </div>
      )}
    </div>
  );
}

export default function Agenti() {
  const [agenti, setAgenti] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | da_pagare | pagati
  const [yearFilter, setYearFilter] = useState(String(CURRENT_YEAR));
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const toggleRow = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/agenti`),
        axios.get(`${API}/agenti/summary`),
      ]);
      setAgenti(aRes.data || []);
      setSummary(sRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento agenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setCurrentPage(1); }, [filter, yearFilter, search]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, beneficiari: [] });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      nome: a.nome || '',
      cognome: a.cognome || '',
      anno: a.anno ?? CURRENT_YEAR,
      iban: a.iban || '',
      imponibile: a.imponibile ?? '',
      percentuale: a.percentuale ?? '',
      provvigioni_maturate: a.provvigioni_maturate ?? '',
      periodo_dal: a.periodo_dal || '',
      periodo_al: a.periodo_al || '',
      pagato: !!a.pagato,
      data_pagamento: a.data_pagamento || '',
      importo_pagato: a.importo_pagato ?? '',
      beneficiari: (a.beneficiari || []).map((b) => ({
        tipo: b.tipo || 'condominio',
        nome: b.nome || '',
        iban: b.iban || '',
        percentuale: b.percentuale ?? '',
        importo_da_pagare: b.importo_da_pagare ?? '',
        pagato: !!b.pagato,
        data_pagamento: b.data_pagamento || '',
      })),
    });
    setDialogOpen(true);
  };

  // Clona: nuovo record con gli stessi dati/importi ma senza stato di pagamento
  // (utile per lo stesso agente in un nuovo periodo/mese).
  const handleClone = (a) => {
    setEditing(null);
    setForm({
      nome: a.nome || '',
      cognome: a.cognome || '',
      anno: CURRENT_YEAR,
      iban: a.iban || '',
      imponibile: a.imponibile ?? '',
      percentuale: a.percentuale ?? '',
      provvigioni_maturate: a.provvigioni_maturate ?? '',
      periodo_dal: '',
      periodo_al: '',
      pagato: false,
      data_pagamento: '',
      importo_pagato: '',
      beneficiari: (a.beneficiari || []).map((b) => ({
        tipo: b.tipo || 'condominio',
        nome: b.nome || '',
        iban: b.iban || '',
        percentuale: b.percentuale ?? '',
        importo_da_pagare: b.importo_da_pagare ?? '',
        pagato: false,
        data_pagamento: '',
      })),
    });
    setDialogOpen(true);
    toast.info('Agente clonato - modifica periodo/importi e salva');
  };

  // beneficiari helpers (form)
  const addBenef = () => setForm((f) => ({ ...f, beneficiari: [...f.beneficiari, { ...emptyBenef }] }));
  const setBenef = (i, nb) => setForm((f) => ({ ...f, beneficiari: f.beneficiari.map((x, j) => (j === i ? nb : x)) }));
  const delBenef = (i) => setForm((f) => ({ ...f, beneficiari: f.beneficiari.filter((_, j) => j !== i) }));

  const impNum = parseFloat(form.imponibile);
  const percNum = parseFloat(form.percentuale);
  const autoCalc = !isNaN(impNum) && !isNaN(percNum);
  const quotaAgente = autoCalc ? r2(impNum * percNum / 100) : (parseFloat(form.provvigioni_maturate) || 0);
  const guadagnoJV = !isNaN(impNum) ? r2(impNum - quotaAgente) : null;

  const benefImporto = (b) => {
    const p = parseFloat(b.percentuale);
    return !isNaN(p) ? r2(quotaAgente * p / 100) : (parseFloat(b.importo_da_pagare) || 0);
  };
  const totBenef = form.beneficiari.reduce((s, b) => s + benefImporto(b), 0);
  const nettoAgente = r2(quotaAgente - totBenef);

  const buildPayload = () => ({
    nome: form.nome.trim().toUpperCase(),
    cognome: form.cognome.trim().toUpperCase(),
    anno: form.anno === '' || form.anno == null ? null : parseInt(form.anno, 10),
    iban: form.iban.trim().toUpperCase() || null,
    imponibile: form.imponibile === '' ? null : parseFloat(form.imponibile),
    percentuale: form.percentuale === '' ? null : parseFloat(form.percentuale),
    provvigioni_maturate: parseFloat(form.provvigioni_maturate) || 0,
    periodo_dal: form.periodo_dal || null,
    periodo_al: form.periodo_al || null,
    pagato: form.pagato,
    data_pagamento: form.pagato ? (form.data_pagamento || null) : null,
    importo_pagato: form.pagato ? (form.importo_pagato === '' ? null : parseFloat(form.importo_pagato)) : null,
    beneficiari: form.beneficiari.map((b) => ({
      tipo: b.tipo,
      nome: b.nome.trim().toUpperCase(),
      iban: b.iban.trim().toUpperCase() || null,
      percentuale: b.percentuale === '' ? null : parseFloat(b.percentuale),
      importo_da_pagare: parseFloat(b.importo_da_pagare) || 0,
      pagato: b.pagato,
      data_pagamento: b.pagato ? (b.data_pagamento || null) : null,
    })),
  });

  const save = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error('Nome e cognome sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/agenti/${editing.id}`, buildPayload());
        toast.success('Agente aggiornato');
      } else {
        await axios.post(`${API}/agenti`, buildPayload());
        toast.success('Agente creato');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'agente ${a.cognome} ${a.nome}?`)) return;
    try {
      await axios.delete(`${API}/agenti/${a.id}`);
      toast.success('Agente eliminato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const years = Array.from(new Set([
    CURRENT_YEAR,
    ...agenti.map((a) => a.anno).filter((y) => y != null),
  ])).sort((x, y) => y - x);

  const filtered = agenti.filter((a) => {
    // filtro anno: confronto numerico; i record senza anno restano sempre visibili
    if (yearFilter !== 'all' && a.anno != null && Number(a.anno) !== Number(yearFilter)) return false;
    if (filter === 'da_pagare' && a.pagato) return false;
    if (filter === 'pagati' && !a.pagato) return false;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      if (!`${a.cognome} ${a.nome}`.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginated = filtered.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Agenti
        </h1>
        <Button onClick={openNew} data-testid="agente-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo agente
        </Button>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Agenti totali</p>
            <p className="text-2xl font-bold text-slate-900">{summary?.totale ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="border-teal-200">
          <CardContent className="pt-6">
            <p className="text-sm text-teal-700">Guadagno JV totale</p>
            <p className="text-2xl font-bold text-teal-700">{summary ? euro(summary.guadagno_jv_totale) : '—'}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-700">Agenti da pagare</p>
            <p className="text-2xl font-bold text-amber-700">
              {summary?.da_pagare_count ?? '—'}
              <span className="text-base font-medium ml-2">{summary ? euro(summary.da_pagare_importo) : ''}</span>
            </p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="pt-6">
            <p className="text-sm text-red-700">Beneficiari da pagare</p>
            <p className="text-2xl font-bold text-red-700">
              {summary?.beneficiari_da_pagare_count ?? '—'}
              <span className="text-base font-medium ml-2">{summary ? euro(summary.beneficiari_da_pagare_importo) : ''}</span>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca per nome o cognome..." value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="agente-search" />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-full sm:w-36" data-testid="agente-year-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli anni</SelectItem>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="agente-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="da_pagare">Da pagare</SelectItem>
            <SelectItem value="pagati">Pagati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Cognome</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Anno</TableHead>
                <TableHead className="text-right">Provvigioni maturate</TableHead>
                <TableHead className="text-right">Guadagno JV</TableHead>
                <TableHead className="text-right">Netto agente</TableHead>
                <TableHead>Periodo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Data pagamento</TableHead>
                <TableHead>Beneficiari</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-slate-400">Caricamento…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-slate-400">Nessun agente</TableCell></TableRow>
              ) : paginated.map((a) => {
                const benef = a.beneficiari || [];
                const daPagare = benef.filter((b) => !b.pagato).length;
                const isOpen = expanded[a.id];
                return (
                  <React.Fragment key={a.id}>
                    <TableRow className={!a.pagato ? 'bg-amber-50' : ''}>
                      <TableCell className="w-8 p-0 text-center">
                        {benef.length > 0 && (
                          <button onClick={() => toggleRow(a.id)} className="p-2 text-slate-400 hover:text-slate-700" data-testid={`agente-expand-${a.id}`}>
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{a.cognome}</TableCell>
                      <TableCell>{a.nome}</TableCell>
                      <TableCell className="text-slate-500">{a.anno ?? '—'}</TableCell>
                      <TableCell className="text-right">
                        {euro(a.provvigioni_maturate)}
                        {a.percentuale != null && <span className="text-xs text-slate-400 ml-1">({a.percentuale}%)</span>}
                        {a.imponibile != null && <span className="block text-xs text-slate-400">imp. {euro(a.imponibile)}</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium text-teal-700">{a.guadagno_jv != null ? euro(a.guadagno_jv) : '—'}</TableCell>
                      <TableCell className="text-right">{a.netto_agente != null ? euro(a.netto_agente) : '—'}</TableCell>
                      <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                        {a.periodo_dal || a.periodo_al ? `${dmy(a.periodo_dal) || '…'} → ${dmy(a.periodo_al) || '…'}` : '—'}
                      </TableCell>
                      <TableCell>
                        {a.pagato
                          ? <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Pagato</Badge>
                          : <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Da pagare</Badge>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{dmy(a.data_pagamento) || '—'}</TableCell>
                      <TableCell>
                        {benef.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <button onClick={() => toggleRow(a.id)} className="inline-flex items-center gap-1 text-sm hover:underline" data-testid={`agente-benef-${a.id}`}>
                            {benef.length}
                            {daPagare > 0 && <span className="w-2 h-2 rounded-full bg-red-500 inline-block" title={`${daPagare} da pagare`} />}
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => handleClone(a)} title="Clona" data-testid={`agente-clone-${a.id}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(a)} title="Modifica" data-testid={`agente-edit-${a.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(a)} title="Elimina" data-testid={`agente-delete-${a.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>

                    {isOpen && benef.length > 0 && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={12} className="p-0 bg-slate-50/60">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-slate-100/80 border-y">
                                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">Tipo</th>
                                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">Nome</th>
                                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-2">Importo</th>
                                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-2">%</th>
                                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">IBAN</th>
                                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">Stato</th>
                                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">Data pagamento</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupByTipo(benef).map((b, i) => (
                                  <tr key={i} className="border-b last:border-0">
                                    <td className="px-4 py-2 text-sm font-medium">{tipoLabel(b.tipo)}</td>
                                    <td className="px-4 py-2 text-sm">{b.nome || '—'}</td>
                                    <td className="px-4 py-2 text-sm text-right">{euro(b.importo_da_pagare)}</td>
                                    <td className="px-4 py-2 text-sm text-right text-slate-500">{b.percentuale != null ? `${b.percentuale}%` : '—'}</td>
                                    <td className="px-4 py-2 text-xs text-slate-500 break-all">{b.iban || '—'}</td>
                                    <td className="px-4 py-2">
                                      {b.pagato
                                        ? <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Pagato</Badge>
                                        : <Badge className="bg-red-100 text-red-700 text-xs"><Clock className="w-3 h-3 mr-1" />Da pagare</Badge>}
                                    </td>
                                    <td className="px-4 py-2 text-sm whitespace-nowrap">{dmy(b.data_pagamento) || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} agenti
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Precedente
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog nuovo/modifica */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              {editing ? 'Modifica agente' : 'Nuovo agente'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value.toUpperCase() })} className="uppercase" data-testid="agente-nome" />
              </div>
              <div className="space-y-2">
                <Label>Cognome</Label>
                <Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value.toUpperCase() })} className="uppercase" data-testid="agente-cognome" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Anno di riferimento</Label>
                <Input type="number" step="1" min="2000" max="2100" value={form.anno}
                  onChange={(e) => setForm({ ...form, anno: e.target.value })} data-testid="agente-anno" />
              </div>
              <div className="space-y-2">
                <Label>IBAN agente <span className="text-slate-400 font-normal">— promemoria</span></Label>
                <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} className="uppercase" placeholder="ITxx..." />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Imponibile (€) <span className="text-slate-400 font-normal">— opz.</span></Label>
                <Input type="number" step="0.01" min="0" value={form.imponibile} placeholder="totale su cui calcolare"
                  onChange={(e) => setForm({ ...form, imponibile: e.target.value })} data-testid="agente-imponibile" />
              </div>
              <div className="space-y-2">
                <Label>Percentuale agente (%) <span className="text-slate-400 font-normal">— opz.</span></Label>
                <Input type="number" step="0.01" min="0" max="100" value={form.percentuale} placeholder="es. 70"
                  onChange={(e) => setForm({ ...form, percentuale: e.target.value })} data-testid="agente-percentuale" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Provvigioni maturate (€) {autoCalc && <span className="text-teal-600 font-normal">— calcolate da imponibile × %</span>}</Label>
              <Input type="number" step="0.01" min="0" value={autoCalc ? quotaAgente : form.provvigioni_maturate} disabled={autoCalc}
                onChange={(e) => setForm({ ...form, provvigioni_maturate: e.target.value })} data-testid="agente-provvigioni" />
            </div>

            {/* Beneficiari da pagare (elenco, es. piu' condomini) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base">Beneficiari da pagare</Label>
                <Button type="button" variant="outline" size="sm" onClick={addBenef} data-testid="agente-add-benef">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              {form.beneficiari.length === 0 && (
                <p className="text-sm text-slate-400">Nessun beneficiario. Aggiungi sub-agente, condominio (anche più di uno) o altra parte.</p>
              )}
              {form.beneficiari.map((b, i) => (
                <BeneficiarioBlock
                  key={i}
                  value={b}
                  onChange={(nb) => setBenef(i, nb)}
                  onRemove={() => delBenef(i)}
                  provvigioni={quotaAgente}
                />
              ))}
            </div>

            <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-3 text-sm space-y-1">
              {!isNaN(impNum) && (
                <div className="flex justify-between"><span>Guadagno JV</span><strong>{euro(guadagnoJV)}</strong></div>
              )}
              <div className="flex justify-between"><span>Quota agente</span><strong>{euro(quotaAgente)}</strong></div>
              {totBenef > 0 && (
                <>
                  <div className="flex justify-between text-slate-500"><span>− beneficiari ({form.beneficiari.length})</span><span>{euro(totBenef)}</span></div>
                  <div className="flex justify-between"><span>Netto agente</span><strong>{euro(nettoAgente)}</strong></div>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Provvigioni — periodo dal</Label>
                <DateInput value={form.periodo_dal} onChange={(val) => setForm({ ...form, periodo_dal: val })} placeholder="gg/mm/aaaa" />
              </div>
              <div className="space-y-2">
                <Label>al</Label>
                <DateInput value={form.periodo_al} onChange={(val) => setForm({ ...form, periodo_al: val })} placeholder="gg/mm/aaaa" />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Agente pagato</Label>
              <Switch checked={form.pagato} onCheckedChange={(v) => setForm({ ...form, pagato: v })} data-testid="agente-pagato" />
            </div>
            {form.pagato && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Data pagamento</Label>
                  <DateInput value={form.data_pagamento} onChange={(val) => setForm({ ...form, data_pagamento: val })} placeholder="gg/mm/aaaa" />
                </div>
                <div className="space-y-2">
                  <Label>Importo pagato (€)</Label>
                  <Input type="number" step="0.01" min="0" value={form.importo_pagato} placeholder="= provvigioni se vuoto"
                    onChange={(e) => setForm({ ...form, importo_pagato: e.target.value })} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="agente-save">
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
